import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  Upload,
  CheckCircle2,
  Circle,
  AlertTriangle,
  Play,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import { importsApi, mappingProfilesApi, templatesApi } from "../api/endpoints";
import { SkeletonCard } from "../components/states/Skeleton";
import { ErrorState } from "../components/states/ErrorState";
import { EmptyState } from "../components/states/EmptyState";
import { FactChip } from "../components/chips/FactChip";
import { resolveError } from "../lib/errorCodes";
import { cn } from "../lib/utils";

/**
 * Import wizard — 7 UI steps mapped onto 5 API states (see PRD §4/§14).
 *
 * UI steps
 *   1  upload           <- API state:  uploaded
 *   2  sheet-mapping    <- API state:  sheet_mapped
 *   3  column-mapping   \  same API call:
 *   4  value-mapping    /  POST /column_mapping (enum_mappings inside)
 *   5  preview          <- API state:  previewed  (POST /preview)
 *   6  validate         <- reads preview_report validation flags
 *   7  commit           <- API state:  committed  (POST /commit)
 *
 * Backend states, canonical:
 *   uploaded → sheet_mapped → column_mapped → previewed → committed
 */

const CANONICAL_ENTITIES = [
  "Processes",
  "Applications",
  "Suppliers",
  "PeopleSkills",
  "Locations",
  "ProcessDependencies",
  "Tests",
];

const UI_STEPS = [
  { key: "upload", label: "Upload" },
  { key: "sheet_mapping", label: "Sheet mapping" },
  { key: "column_mapping", label: "Column mapping" },
  { key: "value_mapping", label: "Value mapping" },
  { key: "preview", label: "Preview" },
  { key: "validate", label: "Validation" },
  { key: "commit", label: "Commit" },
];

const CANONICAL_FIELDS = {
  Processes: [
    "id", "name", "owner", "criticality", "rto_minutes", "rpo_minutes",
    "mtpd_minutes", "mbco_pct", "normal_capacity", "min_required_capacity",
    "min_staff", "normal_staff", "location_id", "manual_workaround_exists",
    "workaround_capacity_pct", "workaround_duration_minutes",
    "workaround_tested", "current_recovery_strategy", "last_bia_review_date",
    "first_material_impact_minutes", "material_impact_indicators",
  ],
  Applications: [
    "id", "process_id", "system_name", "criticality",
    "required_recovery_minutes", "claimed_rta_minutes",
    "demonstrated_rta_minutes", "claimed_rpo_minutes",
    "demonstrated_rpo_minutes", "last_demonstrated_date", "recovery_method",
  ],
  Suppliers: [
    "id", "process_id", "supplier_name", "service",
    "required_recovery_minutes", "contractual_recovery_minutes",
    "alternative_available", "concentration_flag",
  ],
  PeopleSkills: [
    "id", "process_id", "role", "skill", "required_count", "available_count",
    "key_person_flag", "cross_trained_count",
  ],
  Locations: [
    "id", "process_id", "location_name", "required_capacity",
    "primary_capacity", "alternate_capacity", "remote_capable",
  ],
  ProcessDependencies: [
    "id", "upstream_process_id", "downstream_process_id", "dependency_type",
    "required_recovery_relationship",
  ],
  Tests: [
    "id", "process_id", "strategy", "test_date", "scenario",
    "target_recovery_minutes", "demonstrated_recovery_minutes",
    "demonstrated_capacity_pct", "passed", "observations",
  ],
};

const DATATYPES = [
  "string", "integer", "float", "boolean", "date", "duration_minutes",
  "percentage", "criticality", "enum", "id", "id_ref", "list_string",
];

// State-order helper — decides which UI steps are legal to open.
const STATE_ORDER = {
  pending: 0,
  uploaded: 1,
  sheet_mapped: 2,
  column_mapped: 3,
  previewed: 4,
  committed: 5,
  failed: 0,
  abandoned: 0,
};

function stepAllowedForState(stepKey, state) {
  const order = STATE_ORDER[state] ?? 0;
  const map = {
    upload: 0,
    sheet_mapping: 1,
    column_mapping: 2,
    value_mapping: 2,
    preview: 3,
    validate: 4,
    commit: 4,
  };
  return order >= map[stepKey];
}

// ---------------------------------------------------------------------------
// Wizard shell
// ---------------------------------------------------------------------------

export default function ImportWizardPage() {
  const { workspaceId } = useParams();
  const [params, setParams] = useSearchParams();
  const importId = params.get("id") || null;
  const [imp, setImp] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(params.get("step") || "upload");
  const navigate = useNavigate();

  async function refresh(id = importId) {
    if (!id) return;
    try {
      const doc = await importsApi.get(workspaceId, id);
      setImp(doc);
      return doc;
    } catch (e) {
      setError(e);
    }
  }

  useEffect(() => {
    if (importId) refresh(importId);
    else setImp(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importId, workspaceId]);

  useEffect(() => {
    if (!imp) return;
    // Auto-advance the wizard's current step to match the state, up to the
    // user's current step — never regress if the user is doing value_mapping.
    const preferred = defaultStepForState(imp.state);
    const currentOrder = STEP_ORDER[step] ?? 0;
    const preferredOrder = STEP_ORDER[preferred] ?? 0;
    if (currentOrder < preferredOrder) {
      setStep(preferred);
      setParams({ id: imp.id, step: preferred }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imp?.state]);

  function goStep(next) {
    setStep(next);
    setParams(
      importId ? { id: importId, step: next } : { step: next },
      { replace: true },
    );
  }

  const stepIndex = UI_STEPS.findIndex((s) => s.key === step);

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">
            Import wizard
          </div>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900">
            {imp?.filename || "New import"}
          </h1>
          {imp && (
            <div className="mt-1 text-xs font-mono text-slate-500">
              state:{" "}
              <span
                data-testid="wizard-state"
                className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-700"
              >
                {imp.state}
              </span>
            </div>
          )}
        </div>
        <a
          href={templatesApi.canonicalUrl(true)}
          data-testid="canonical-template-link"
          className="text-xs text-slate-600 underline hover:text-slate-900"
        >
          Download canonical template
        </a>
      </header>

      <Stepper current={step} state={imp?.state || "pending"} onNavigate={goStep} />

      {error && !imp && (
        <ErrorState
          error={error}
          onRetry={() => (importId ? refresh(importId) : setError(null))}
        />
      )}

      {step === "upload" && (
        <UploadStep
          workspaceId={workspaceId}
          existing={imp}
          onUploaded={(doc) => {
            setImp(doc);
            setParams({ id: doc.id, step: "sheet_mapping" }, { replace: true });
            setStep("sheet_mapping");
          }}
        />
      )}

      {step !== "upload" && !imp && <SkeletonCard />}

      {step === "sheet_mapping" && imp && (
        <SheetMappingStep
          workspaceId={workspaceId}
          imp={imp}
          onSaved={async (updated) => {
            setImp(updated);
            goStep("column_mapping");
          }}
        />
      )}
      {step === "column_mapping" && imp && (
        <ColumnMappingStep
          workspaceId={workspaceId}
          imp={imp}
          onNext={() => goStep("value_mapping")}
          onSaved={setImp}
        />
      )}
      {step === "value_mapping" && imp && (
        <ValueMappingStep
          workspaceId={workspaceId}
          imp={imp}
          onNext={async () => {
            goStep("preview");
          }}
          onSaved={setImp}
        />
      )}
      {step === "preview" && imp && (
        <PreviewStep
          workspaceId={workspaceId}
          imp={imp}
          onPreviewed={async () => {
            const doc = await refresh(imp.id);
            if (doc) goStep("validate");
          }}
        />
      )}
      {step === "validate" && imp && (
        <ValidationStep
          workspaceId={workspaceId}
          imp={imp}
          onGoBack={() => goStep("column_mapping")}
          onProceed={() => goStep("commit")}
        />
      )}
      {step === "commit" && imp && (
        <CommitStep
          workspaceId={workspaceId}
          imp={imp}
          onCommitted={(runId) => {
            if (runId) navigate(`/w/${workspaceId}/analyses/${runId}`);
          }}
        />
      )}

      {imp && (
        <div className="pt-4 border-t border-slate-100 text-xs text-slate-500">
          Need to start over?{" "}
          <button
            type="button"
            data-testid="wizard-restart"
            className="underline hover:text-slate-900"
            onClick={() => {
              setImp(null);
              setStep("upload");
              setParams({}, { replace: true });
            }}
          >
            Start a new import
          </button>
          .
        </div>
      )}
    </div>
  );
}

const STEP_ORDER = UI_STEPS.reduce(
  (acc, s, i) => ({ ...acc, [s.key]: i }),
  {},
);

function defaultStepForState(state) {
  switch (state) {
    case "uploaded":
      return "sheet_mapping";
    case "sheet_mapped":
      return "column_mapping";
    case "column_mapped":
      return "preview";
    case "previewed":
      return "validate";
    case "committed":
      return "commit";
    default:
      return "upload";
  }
}

// ---------------------------------------------------------------------------
// Stepper
// ---------------------------------------------------------------------------

function Stepper({ current, state, onNavigate }) {
  return (
    <ol
      data-testid="wizard-stepper"
      className="flex flex-wrap gap-2 rounded border border-slate-200 bg-white p-3"
    >
      {UI_STEPS.map((s, i) => {
        const active = s.key === current;
        const allowed = stepAllowedForState(s.key, state);
        return (
          <li key={s.key}>
            <button
              type="button"
              data-testid={`wizard-step-${s.key}`}
              disabled={!allowed}
              aria-current={active ? "step" : undefined}
              onClick={() => allowed && onNavigate(s.key)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs",
                active
                  ? "bg-slate-900 text-white"
                  : allowed
                    ? "bg-slate-100 text-slate-800 hover:bg-slate-200"
                    : "bg-slate-50 text-slate-400 cursor-not-allowed",
              )}
            >
              {allowed ? (
                <CheckCircle2 size={12} />
              ) : (
                <Circle size={12} />
              )}
              <span>
                {i + 1}. {s.label}
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Step 1 · Upload
// ---------------------------------------------------------------------------

function UploadStep({ workspaceId, existing, onUploaded }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [file, setFile] = useState(null);
  const [profiles, setProfiles] = useState(null);

  useEffect(() => {
    mappingProfilesApi.list(workspaceId).then(setProfiles).catch(() => setProfiles([]));
  }, [workspaceId]);

  async function onUpload() {
    if (!file) return;
    setBusy(true);
    setErr(null);
    try {
      const doc = await importsApi.upload(workspaceId, file);
      onUploaded(doc);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="step-upload"
      className="rounded border border-slate-200 bg-white p-6 space-y-4"
    >
      <div className="flex items-start gap-3">
        <Upload size={20} className="text-slate-500 mt-1" />
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            1 · Upload your BIA workbook
          </h3>
          <p className="text-xs text-slate-500 mt-1 max-w-lg">
            Accepts .xlsx and .csv. Macros, embedded objects, and OLE content
            are rejected. If the file has multiple sheets, you'll classify
            each one in the next step.
          </p>
        </div>
      </div>

      <label className="block">
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.csv"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid="upload-file-input"
          className="block w-full text-sm text-slate-700 file:mr-3 file:rounded file:border file:border-slate-300 file:bg-slate-50 file:px-3 file:py-1.5 file:text-sm hover:file:bg-slate-100"
        />
      </label>

      {err && <WizardError err={err} />}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-xs text-slate-500">
          {profiles?.length
            ? `${profiles.length} saved mapping profile${profiles.length === 1 ? "" : "s"} available.`
            : "No saved mapping profiles yet."}
        </div>
        <button
          type="button"
          disabled={!file || busy}
          onClick={onUpload}
          data-testid="upload-submit"
          className="inline-flex items-center gap-2 rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
        >
          {busy ? "Uploading…" : "Upload & continue"} <ArrowRight size={14} />
        </button>
      </div>

      {existing && (
        <p className="text-[11px] text-slate-500">
          A previous import ({existing.filename}) is currently open. Uploading
          a new file will replace it in this wizard session.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 · Sheet mapping
// ---------------------------------------------------------------------------

function SheetMappingStep({ workspaceId, imp, onSaved }) {
  const sheets = imp.sheets_info || [];
  const initial = {};
  for (const s of sheets) {
    const existing = (imp.sheet_mapping || []).find((m) => m.sheet_name === s.name);
    initial[s.name] = existing?.entity || s.entity_suggestion || "Ignore";
  }
  const [mapping, setMapping] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function onSave() {
    setBusy(true);
    setErr(null);
    try {
      const doc = await importsApi.sheetMapping(
        workspaceId,
        imp.id,
        Object.entries(mapping).map(([sheet_name, entity]) => ({
          sheet_name,
          entity,
        })),
      );
      onSaved(doc);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="step-sheet-mapping"
      className="rounded border border-slate-200 bg-white p-6 space-y-4"
    >
      <div>
        <h3 className="text-sm font-semibold text-slate-900">
          2 · Classify each sheet
        </h3>
        <p className="text-xs text-slate-500 mt-1 max-w-lg">
          Pick which canonical entity each sheet contains. Unknown sheets can
          be safely ignored.
        </p>
      </div>

      {sheets.length === 0 ? (
        <EmptyState title="No sheets detected." />
      ) : (
        <table
          className="w-full text-sm"
          data-testid="sheet-mapping-table"
        >
          <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="py-2">Sheet</th>
              <th className="py-2">Rows × cols</th>
              <th className="py-2">Suggested</th>
              <th className="py-2">Entity</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sheets.map((s) => (
              <tr key={s.name}>
                <td className="py-2 font-medium text-slate-900">{s.name}</td>
                <td className="py-2 text-slate-600 font-mono text-xs">
                  {s.row_count} × {s.col_count}
                </td>
                <td className="py-2 text-slate-600 text-xs">
                  {s.entity_suggestion || "—"}
                </td>
                <td className="py-2">
                  <select
                    data-testid={`sheet-entity-${s.name}`}
                    value={mapping[s.name] || "Ignore"}
                    onChange={(e) =>
                      setMapping({ ...mapping, [s.name]: e.target.value })
                    }
                    className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
                  >
                    <option value="Ignore">Ignore</option>
                    {CANONICAL_ENTITIES.map((e) => (
                      <option key={e} value={e}>
                        {e}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {err && <WizardError err={err} />}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onSave}
          disabled={busy}
          data-testid="sheet-mapping-save"
          className="inline-flex items-center gap-2 rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
        >
          {busy ? "Saving…" : "Save mapping & continue"} <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 · Column mapping (per sheet)
// ---------------------------------------------------------------------------

function ColumnMappingStep({ workspaceId, imp, onNext, onSaved }) {
  const mappedSheets = (imp.sheet_mapping || []).filter(
    (m) => m.entity && m.entity !== "Ignore",
  );
  const [activeSheet, setActiveSheet] = useState(
    mappedSheets[0]?.sheet_name || "",
  );
  return (
    <div
      data-testid="step-column-mapping"
      className="rounded border border-slate-200 bg-white p-6 space-y-4"
    >
      <div>
        <h3 className="text-sm font-semibold text-slate-900">
          3 · Map columns to canonical fields
        </h3>
        <p className="text-xs text-slate-500 mt-1 max-w-lg">
          Enum values (Criticality tiers, booleans) will be captured on the
          next step. All of column mapping and value mapping is saved together
          in a single API call.
        </p>
      </div>

      {mappedSheets.length === 0 ? (
        <EmptyState title="No sheets are mapped to an entity yet." />
      ) : (
        <div className="flex gap-2 flex-wrap">
          {mappedSheets.map((m) => (
            <button
              key={m.sheet_name}
              type="button"
              onClick={() => setActiveSheet(m.sheet_name)}
              data-testid={`col-map-tab-${m.sheet_name}`}
              className={cn(
                "rounded px-2.5 py-1 text-xs",
                activeSheet === m.sheet_name
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-800 hover:bg-slate-200",
              )}
            >
              {m.sheet_name} <span className="opacity-60">· {m.entity}</span>
            </button>
          ))}
        </div>
      )}

      {activeSheet && (
        <ColumnMappingForm
          key={activeSheet}
          workspaceId={workspaceId}
          imp={imp}
          sheet={mappedSheets.find((m) => m.sheet_name === activeSheet)}
          onSaved={onSaved}
        />
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onNext}
          data-testid="column-mapping-next"
          disabled={imp.state !== "column_mapped" && imp.state !== "previewed"}
          title={
            imp.state === "column_mapped" || imp.state === "previewed"
              ? ""
              : "Save at least one column mapping first"
          }
          className="inline-flex items-center gap-2 rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
        >
          Continue to value mapping <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

function ColumnMappingForm({ workspaceId, imp, sheet, onSaved }) {
  const sheetInfo = (imp.sheets_info || []).find((s) => s.name === sheet.sheet_name);
  const headers = sheetInfo?.headers || [];
  const suggestions = imp.column_suggestions?.[sheet.sheet_name] || {};
  const existing = imp.column_mapping?.[sheet.sheet_name] || [];
  const entity = sheet.entity;
  const canonicalFields = CANONICAL_FIELDS[entity] || [];

  const [rows, setRows] = useState(() =>
    headers.map((h) => {
      const found = existing.find((c) => c.source_header === h);
      return {
        source_header: h,
        target_field: found?.target_field || suggestions[h] || "",
        datatype: found?.datatype || "",
        unit: found?.unit || null,
        allow_over_100: found?.allow_over_100 || false,
        ignore: found?.ignore || false,
      };
    }),
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function onSave() {
    setBusy(true);
    setErr(null);
    try {
      const doc = await importsApi.columnMapping(workspaceId, imp.id, {
        sheet_name: sheet.sheet_name,
        columns: rows,
        enum_mappings: imp.enum_mapping || {},
      });
      onSaved(doc);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }

  function updateRow(i, next) {
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...next } : r)));
  }

  return (
    <div className="space-y-3">
      <table
        className="w-full text-sm"
        data-testid="column-mapping-form"
      >
        <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="py-2">Source column</th>
            <th className="py-2">Canonical field</th>
            <th className="py-2">Datatype</th>
            <th className="py-2 text-right">Ignore</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <tr key={r.source_header}>
              <td className="py-1.5 text-slate-900">
                <div className="font-mono text-xs">{r.source_header}</div>
                {suggestions[r.source_header] && (
                  <div className="text-[10px] text-slate-500">
                    Suggested: {suggestions[r.source_header]}
                  </div>
                )}
              </td>
              <td className="py-1.5">
                <select
                  data-testid={`col-target-${r.source_header}`}
                  disabled={r.ignore}
                  value={r.target_field || ""}
                  onChange={(e) =>
                    updateRow(i, { target_field: e.target.value })
                  }
                  className="rounded border border-slate-300 bg-white px-2 py-1 text-sm w-56"
                >
                  <option value="">(unmapped)</option>
                  {canonicalFields.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </td>
              <td className="py-1.5">
                <select
                  data-testid={`col-type-${r.source_header}`}
                  disabled={r.ignore}
                  value={r.datatype || ""}
                  onChange={(e) => updateRow(i, { datatype: e.target.value })}
                  className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
                >
                  <option value="">(auto)</option>
                  {DATATYPES.map((dt) => (
                    <option key={dt} value={dt}>
                      {dt}
                    </option>
                  ))}
                </select>
              </td>
              <td className="py-1.5 text-right">
                <input
                  type="checkbox"
                  data-testid={`col-ignore-${r.source_header}`}
                  checked={r.ignore}
                  onChange={(e) => updateRow(i, { ignore: e.target.checked })}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {err && <WizardError err={err} />}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onSave}
          disabled={busy}
          data-testid="column-mapping-save"
          className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-800 hover:bg-slate-100 disabled:opacity-60"
        >
          {busy ? "Saving…" : `Save mapping for ${sheet.sheet_name}`}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 4 · Value mapping (enums, criticality, booleans) — same API call
// ---------------------------------------------------------------------------

function ValueMappingStep({ workspaceId, imp, onNext, onSaved }) {
  const mappedSheets = (imp.sheet_mapping || []).filter(
    (m) => m.entity && m.entity !== "Ignore",
  );
  const [activeSheet, setActiveSheet] = useState(mappedSheets[0]?.sheet_name || "");
  const [enums, setEnums] = useState(() => cloneEnums(imp.enum_mapping || {}));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const activeSheetMap = imp.column_mapping?.[activeSheet] || [];
  // Fields that likely need value mapping: criticality, boolean, enum, id_ref
  // (with references not present in dataset, though we only surface enums here).
  const criticalityCols = activeSheetMap.filter((c) => c.datatype === "criticality");
  const booleanCols = activeSheetMap.filter((c) => c.datatype === "boolean");
  const enumCols = activeSheetMap.filter((c) => c.datatype === "enum");
  const relevant = [...criticalityCols, ...booleanCols, ...enumCols];

  async function onSave() {
    setBusy(true);
    setErr(null);
    try {
      const columns = imp.column_mapping?.[activeSheet] || [];
      const doc = await importsApi.columnMapping(workspaceId, imp.id, {
        sheet_name: activeSheet,
        columns,
        enum_mappings: enums,
      });
      onSaved(doc);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }

  function updateEnum(field, sourceValue, canonicalValue) {
    setEnums((prev) => ({
      ...prev,
      [field]: { ...(prev[field] || {}), [sourceValue]: canonicalValue },
    }));
  }

  return (
    <div
      data-testid="step-value-mapping"
      className="rounded border border-slate-200 bg-white p-6 space-y-4"
    >
      <div>
        <h3 className="text-sm font-semibold text-slate-900">
          4 · Map source values to canonical values
        </h3>
        <p className="text-xs text-slate-500 mt-1 max-w-lg">
          Only enum, criticality, and boolean columns are shown here. This is
          the same API call as column mapping — nothing new hits the server
          until you save.
        </p>
      </div>

      {mappedSheets.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {mappedSheets.map((m) => (
            <button
              key={m.sheet_name}
              type="button"
              onClick={() => setActiveSheet(m.sheet_name)}
              data-testid={`val-map-tab-${m.sheet_name}`}
              className={cn(
                "rounded px-2.5 py-1 text-xs",
                activeSheet === m.sheet_name
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-800 hover:bg-slate-200",
              )}
            >
              {m.sheet_name}
            </button>
          ))}
        </div>
      )}

      {relevant.length === 0 ? (
        <EmptyState title="No enum-like columns on this sheet — nothing to map." />
      ) : (
        <div className="space-y-4">
          {relevant.map((col) => (
            <EnumMappingCard
              key={col.source_header}
              sheet={activeSheet}
              column={col}
              headers={imp.sheets_info?.find((s) => s.name === activeSheet)?.headers || []}
              sampleRows={
                imp.sheets_info?.find((s) => s.name === activeSheet)
                  ?.sample_rows || []
              }
              values={enums[col.target_field] || {}}
              onChange={(sv, cv) => updateEnum(col.target_field, sv, cv)}
            />
          ))}
        </div>
      )}

      {err && <WizardError err={err} />}

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onSave}
          disabled={busy}
          data-testid="value-mapping-save"
          className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-800 hover:bg-slate-100 disabled:opacity-60"
        >
          {busy ? "Saving…" : "Save value mappings"}
        </button>
        <button
          type="button"
          onClick={onNext}
          data-testid="value-mapping-next"
          className="inline-flex items-center gap-2 rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
        >
          Continue to preview <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

function EnumMappingCard({ column, sampleRows, values, onChange }) {
  // Distinct source values encountered across sample rows for the column.
  const distinct = useMemo(() => {
    const set = new Set();
    for (const r of sampleRows) {
      const v = r[column.source_header];
      if (v !== null && v !== undefined && v !== "") {
        set.add(String(v));
      }
    }
    return Array.from(set);
  }, [sampleRows, column.source_header]);
  const canonicalTargets =
    column.datatype === "boolean"
      ? ["true", "false"]
      : column.datatype === "criticality"
        ? ["CRITICAL", "HIGH", "MEDIUM", "LOW"]
        : []; // free-form for other enums
  return (
    <div
      data-testid={`enum-map-${column.source_header}`}
      className="rounded border border-slate-200 bg-white p-3"
    >
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <FactChip source="SOURCE_FACT" />
        <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-slate-700">
          {column.source_header} → {column.target_field}
        </code>
        <span className="uppercase tracking-widest text-[10px]">
          {column.datatype}
        </span>
      </div>
      {distinct.length === 0 ? (
        <div className="mt-2 text-xs text-slate-500 italic">
          No sample values found — mappings will be applied only when the value
          appears in the workbook.
        </div>
      ) : (
        <table className="mt-2 w-full text-xs">
          <thead className="text-left text-slate-500">
            <tr>
              <th className="py-1">Source value</th>
              <th className="py-1">Canonical value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {distinct.map((v) => (
              <tr key={v}>
                <td className="py-1 font-mono text-slate-800">{v}</td>
                <td className="py-1">
                  {canonicalTargets.length > 0 ? (
                    <select
                      data-testid={`enum-val-${column.target_field}-${v}`}
                      value={values[v] || ""}
                      onChange={(e) => onChange(v, e.target.value)}
                      className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
                    >
                      <option value="">(unmapped)</option>
                      {canonicalTargets.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      data-testid={`enum-val-${column.target_field}-${v}`}
                      value={values[v] || ""}
                      onChange={(e) => onChange(v, e.target.value)}
                      className="rounded border border-slate-300 bg-white px-2 py-1 text-sm w-48"
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 5 · Preview
// ---------------------------------------------------------------------------

function PreviewStep({ workspaceId, imp, onPreviewed }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(imp.preview_report || null);
  const [err, setErr] = useState(null);

  async function onRun() {
    setBusy(true);
    setErr(null);
    try {
      const body = await importsApi.preview(workspaceId, imp.id);
      setPreview(body);
      onPreviewed();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="step-preview"
      className="rounded border border-slate-200 bg-white p-6 space-y-4"
    >
      <div>
        <h3 className="text-sm font-semibold text-slate-900">
          5 · Preview normalisation
        </h3>
        <p className="text-xs text-slate-500 mt-1 max-w-lg">
          The pipeline runs against your workbook without persisting a run.
          Numbers, durations, percentages, and enums are normalised. Anything
          ambiguous is surfaced as an error, never silently interpreted.
        </p>
      </div>

      <button
        type="button"
        onClick={onRun}
        disabled={busy}
        data-testid="preview-run"
        className="inline-flex items-center gap-2 rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
      >
        <Play size={14} />
        {busy ? "Previewing…" : "Run preview"}
      </button>

      {err && <WizardError err={err} />}

      {preview && (
        <PreviewSummary preview={preview} />
      )}
    </div>
  );
}

function PreviewSummary({ preview }) {
  const counts = preview.counts || {};
  const vr = preview.validation_report || {};
  return (
    <div
      data-testid="preview-summary"
      className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
    >
      {Object.entries(counts).map(([k, v]) => (
        <div
          key={k}
          className="rounded border border-slate-200 bg-slate-50 p-3"
          data-testid={`preview-count-${k}`}
        >
          <div className="text-[10px] uppercase tracking-widest text-slate-500">
            {k}
          </div>
          <div className="mt-1 text-xl font-semibold text-slate-900">{v}</div>
        </div>
      ))}
      {typeof vr.rejected_rows === "number" && (
        <div className="rounded border border-slate-200 bg-slate-50 p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">
            rejected rows
          </div>
          <div
            data-testid="preview-rejected"
            className={cn(
              "mt-1 text-xl font-semibold",
              vr.rejected_rows > 0 ? "text-red-800" : "text-slate-900",
            )}
          >
            {vr.rejected_rows}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 6 · Validation
// ---------------------------------------------------------------------------

function ValidationStep({ imp, onGoBack, onProceed }) {
  const vr = imp.preview_report?.validation_report;
  if (!vr) {
    return (
      <div data-testid="step-validation" className="rounded border border-slate-200 bg-white p-6 space-y-3">
        <EmptyState title="Run a preview first to see validation results." />
      </div>
    );
  }
  const errors = vr.errors || [];
  const warnings = vr.warnings || [];
  const canProceed = errors.length === 0;
  return (
    <div
      data-testid="step-validation"
      className="rounded border border-slate-200 bg-white p-6 space-y-4"
    >
      <div>
        <h3 className="text-sm font-semibold text-slate-900">
          6 · Validation
        </h3>
        <p className="text-xs text-slate-500 mt-1 max-w-lg">
          Fix each error before committing. Warnings do not block commit —
          the engine will still ingest the row.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label="Rows accepted" value={vr.accepted_rows} />
        <StatCard label="Rows rejected" value={vr.rejected_rows} negative />
        <StatCard label="Duplicate conflicts" value={vr.duplicate_conflicts} />
        <StatCard label="Broken references" value={vr.broken_references} />
        <StatCard label="Source conflicts" value={vr.source_conflicts} />
        <StatCard label="Unresolved durations" value={vr.unresolved_durations} />
      </div>

      {errors.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-red-800 mb-1">
            Errors ({errors.length})
          </div>
          <ul
            className="space-y-1 max-h-56 overflow-y-auto text-xs"
            data-testid="validation-errors"
          >
            {errors.slice(0, 100).map((e, i) => (
              <li
                key={i}
                className="rounded border border-red-200 bg-red-50 px-3 py-2 font-mono text-red-900"
              >
                <span className="uppercase text-[10px] tracking-widest text-red-700 mr-2">
                  {e.code}
                </span>
                {e.sheet}
                {e.source_row ? `:${e.source_row}` : ""}
                {e.field ? ` · ${e.field}` : ""} — {e.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      {warnings.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-amber-800 mb-1">
            Warnings ({warnings.length})
          </div>
          <ul
            className="space-y-1 max-h-56 overflow-y-auto text-xs"
            data-testid="validation-warnings"
          >
            {warnings.slice(0, 100).map((w, i) => (
              <li
                key={i}
                className="rounded border border-amber-200 bg-amber-50 px-3 py-2 font-mono text-amber-900"
              >
                <span className="uppercase text-[10px] tracking-widest text-amber-700 mr-2">
                  {w.code}
                </span>
                {w.sheet}
                {w.source_row ? `:${w.source_row}` : ""}
                {w.field ? ` · ${w.field}` : ""} — {w.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onGoBack}
          data-testid="validation-back"
          className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
        >
          <ArrowLeft size={14} />
          Back to mapping
        </button>
        <button
          type="button"
          onClick={onProceed}
          disabled={!canProceed}
          data-testid="validation-proceed"
          title={canProceed ? "" : "Resolve errors before committing."}
          className="inline-flex items-center gap-2 rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
        >
          Commit & run analysis <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 7 · Commit
// ---------------------------------------------------------------------------

function CommitStep({ workspaceId, imp, onCommitted }) {
  const [runName, setRunName] = useState("");
  const [profileName, setProfileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [commit, setCommit] = useState(imp.commit || null);
  const idempotencyRef = useRef(
    imp.commit?.idempotency_key ||
      (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}`),
  );

  async function onCommit() {
    setBusy(true);
    setErr(null);
    try {
      const body = await importsApi.commit(workspaceId, imp.id, {
        idempotency_key: idempotencyRef.current,
        run_name: runName || null,
        save_profile_name: profileName || null,
      });
      setCommit(body);
      onCommitted(body.run_id);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="step-commit"
      className="rounded border border-slate-200 bg-white p-6 space-y-4"
    >
      <div>
        <h3 className="text-sm font-semibold text-slate-900">7 · Commit</h3>
        <p className="text-xs text-slate-500 mt-1 max-w-lg">
          This will persist the canonical dataset and run the deterministic
          engine. Historical runs are immutable — the mapping and rule library
          are snapshotted by content hash.
        </p>
      </div>

      {!commit && (
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-xs text-slate-600">
            <span className="block mb-1 uppercase tracking-wider text-[10px] text-slate-500">
              Run name (optional)
            </span>
            <input
              value={runName}
              onChange={(e) => setRunName(e.target.value)}
              data-testid="commit-run-name"
              className="rounded border border-slate-300 bg-white px-2 py-1 text-sm w-full"
              placeholder={`Import ${imp.filename}`}
            />
          </label>
          <label className="text-xs text-slate-600">
            <span className="block mb-1 uppercase tracking-wider text-[10px] text-slate-500">
              Save mapping profile as (optional)
            </span>
            <input
              value={profileName}
              onChange={(e) => setProfileName(e.target.value)}
              data-testid="commit-profile-name"
              className="rounded border border-slate-300 bg-white px-2 py-1 text-sm w-full"
              placeholder="e.g. Northstar mapping v2"
            />
          </label>
        </div>
      )}

      {err && <WizardError err={err} />}

      {!commit && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onCommit}
            disabled={busy}
            data-testid="commit-submit"
            className="inline-flex items-center gap-2 rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {busy ? "Committing…" : "Commit & run engine"}
          </button>
        </div>
      )}

      {commit && (
        <div
          data-testid="commit-summary"
          className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 space-y-1"
        >
          <div className="flex items-center gap-2 font-semibold">
            <CheckCircle2 size={14} /> Committed successfully.
          </div>
          <div className="font-mono text-xs text-emerald-900">
            run_id: {commit.run_id}
          </div>
          {commit.run_id && (
            <Link
              to={`/w/${workspaceId}/analyses/${commit.run_id}`}
              data-testid="commit-open-run"
              className="text-xs underline"
            >
              Open the analysis run →
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function StatCard({ label, value, negative }) {
  return (
    <div className="rounded border border-slate-200 bg-slate-50 p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">
        {label}
      </div>
      <div
        className={cn(
          "mt-1 text-lg font-semibold",
          negative && value > 0 ? "text-red-800" : "text-slate-900",
        )}
      >
        {value ?? 0}
      </div>
    </div>
  );
}

function WizardError({ err }) {
  const resolved = resolveError(err);
  return (
    <div
      role="alert"
      data-testid="wizard-error"
      className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-900 space-y-1"
    >
      <div className="flex items-center gap-2">
        <AlertTriangle size={14} />
        <span>{resolved.message}</span>
      </div>
      <div className="flex items-center gap-2 text-[11px]">
        <code className="rounded bg-red-100 px-1.5 py-0.5 font-mono text-red-900">
          {resolved.code}
        </code>
        {resolved.hint && <span className="text-red-800">{resolved.hint}</span>}
      </div>
      {resolved.isEntitlement && (
        <div className="pt-1">
          <a
            href="mailto:sales@recoveryassure.io?subject=Upgrade%20plan"
            data-testid="wizard-entitlement-cta"
            className="text-xs underline font-medium text-red-900"
          >
            Contact us to upgrade
          </a>
        </div>
      )}
    </div>
  );
}

function cloneEnums(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj || {})) {
    out[k] = { ...v };
  }
  return out;
}
